import React, { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

export default function DataCurrencyTab({ api, scope, formatDate, refreshKey, enabled = true }) {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(null);
  const scopeKey = JSON.stringify(scope);
  const [selection, setSelection] = useState({ scopeKey, page: 1 });
  const page = selection.scopeKey === scopeKey ? selection.page : 1;
  const setPage = nextPage => setSelection({ scopeKey, page: nextPage });

  useEffect(() => {
    if (!enabled) return undefined;
    let current = true;
    setResult(null);
    api.get('/crystal-ball/sources', { params: { ...scope, summaryOnly: true, currencyOnly: true, page }, timeout: 120000 })
      .then(response => {
        if (current) setResult({ data: response.data });
      })
      .catch(error => {
        if (!current) return;
        const reason = error.response?.status ? `HTTP ${error.response.status}`
          : error.code === 'ECONNABORTED' ? 'request timed out' : 'connection or response error';
        setResult({ error: reason });
      });
    return () => { current = false; };
  }, [api, scope, attempt, refreshKey, enabled, page]);

  if (!enabled) return <div className="crystal-ball__loading">Select an included site to load Data Currency.</div>;
  if (!result || result.error) return <div className="crystal-ball__loading" role="status" aria-live="polite">
    {result?.error ? <AlertTriangle size={15} /> : <RefreshCw size={15} />}
    <span>{result?.error ? `Data Currency could not be loaded (${result.error}).` : 'Data Currency is loading...'}</span>
    {result?.error ? <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry loading</button> : null}
  </div>;

  // The fallback keeps this view compatible while the backend deployment catches up.
  const currency = result.data?.currency || Object.values(result.data?.latestByType || {});
  const reportTime = row => new Date(row.reportGeneratedAt || '').getTime() || 0;
  const sorted = [...currency].sort((left, right) => reportTime(right) - reportTime(left)
    || String(left.location || '').localeCompare(String(right.location || ''))
    || String(left.reportType || '').localeCompare(String(right.reportType || '')));
  const pagination = result.data?.pagination || {
    page, pageSize: 25, total: currency.length, totalPages: Math.max(1, Math.ceil(currency.length / 25)),
  };
  const rows = result.data?.pagination ? currency : sorted.slice((page - 1) * 25, page * 25);
  const age = value => {
    const date = value ? new Date(value).getTime() : NaN;
    if (!Number.isFinite(date)) return '--';
    const days = Math.floor((Date.now() - date) / 86400000);
    return days < 0 ? 'Future report date' : days === 0 ? 'Less than 1 day' : `${days} day${days === 1 ? '' : 's'}`;
  };
  return <div className="crystal-ball__single-view">
    <div className="crystal-ball__section-heading">
      <div><h2>Data Currency</h2><p>Newest approved report by site and type. This does not show whether every tail is current.</p></div>
      <button type="button" onClick={() => { setPage(1); setAttempt(value => value + 1); }}><RefreshCw size={15} /> Refresh Data Currency</button>
    </div>
    <div className="crystal-ball__table-scroll">
      <table>
        <thead><tr><th>Site</th><th>Report Type</th><th aria-sort="descending">Latest Report Date ↓</th><th>Report Age</th></tr></thead>
        <tbody>{rows.length ? rows.map(row => <tr key={`${row.location}-${row.reportType}`}>
          <td>{row.location || scope.location}</td><td>{row.reportType}</td>
          <td>{row.reportGeneratedAt ? formatDate(row.reportGeneratedAt, true) : 'Report date unavailable'}</td>
          <td>{age(row.reportGeneratedAt)}</td>
        </tr>) : <tr><td colSpan="4">No approved reports for the selected sites.</td></tr>}</tbody>
      </table>
    </div>
    <nav className="crystal-ball__capacity-pagination" aria-label="Data Currency pages">
      <span>{pagination.total ? `${(pagination.page - 1) * pagination.pageSize + 1}–${(pagination.page - 1) * pagination.pageSize + rows.length} of ${pagination.total}` : '0 rows'}</span>
      <div>
        <button type="button" disabled={pagination.page <= 1} onClick={() => setPage(pagination.page - 1)}>Previous</button>
        <strong>Page {pagination.page} of {pagination.totalPages}</strong>
        <button type="button" disabled={pagination.page >= pagination.totalPages} onClick={() => setPage(pagination.page + 1)}>Next</button>
      </div>
    </nav>
  </div>;
}
